import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import DashTable, {
  Notice,
  Oid,
  PageHead,
  Sub2,
  Tag,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const inr = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PAGE_SIZE = 15;

/* OA6 set dates, OA7 mark ready to ship, OA8 order held. */
const ReadyToShip = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [page, setPage] = useState(1);

  const fetchRows = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchReadyToShip(token, {
        page: targetPage,
        limit: PAGE_SIZE,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching the build list");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const toItem = (row, id) => ({
    id,
    orderId: row.orderId,
    orderNumber: row.orderNumber,
    clinicName: row.clinicName,
    stage: row.stage,
    context: {
      serial: row.serial,
      balanceDue: row.balanceDue,
      bagRequired: row.bagRequired,
      orderTotal: row.orderTotal,
      advanceReceived: row.advanceReceived,
      readyDaysAgo: row.readyDaysAgo,
      reminderStatus: row.reminderStatus,
      gstin: row.gstin,
      paymentType: row.paymentType,
      emiStatus: row.emiStatus,
      balanceDueDate: row.balanceDueDate,
    },
  });

  const handleResend = async (row) => {
    setResendingId(row.orderId);
    try {
      await apiService.resendBalanceLink(row.orderId, token);
      toast.success("Balance payment link resent");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Could not resend the balance link",
      );
    } finally {
      setResendingId(null);
    }
  };


  /* The action a row offers depends on how far through pre-dispatch it is.
     Button weight matches the UI spec: solid for the blocking dispatch-gate
     actions (hold, dispatch), ghost for the lighter build-stage ones. */
  const actionFor = (row) => {
    // A dispatched order is done only once the balance has actually cleared.
    // It can legitimately ship with an unpaid balance (OA8 hold released), so
    // that case still needs to be chaseable from here, not hidden.
    if (row.stage === "Dispatched") {
      if (row.balanceCleared) return null;
      return { id: "SHARE_BALANCE_LINK", label: "Share balance link", screen: "share-balance-link", variant: "ghost" };
    }
    if (row.held) return { id: "OA8", label: "Open hold", screen: "order-hold", variant: "solid" };
    if (!row.datesSet)
      return { id: "OA6", label: "Set dates", screen: "set-dates", variant: "ghost" };
    if (!row.readyToShip)
      return { id: "OA7", label: "Mark ready", screen: "ready-to-ship", variant: "ghost" };
    // held is exactly readyToShip && !balanceCleared && not released, and we
    // already returned above if held — so reaching here with the balance
    // still outstanding only happens once Ops Associate has released the
    // hold (OA8). The backend already allows dispatching in that case, this
    // just needed to actually be reachable from the UI.
    return { id: "OA9", label: "Dispatch", screen: "dispatch", variant: "solid" };
  };

  const columns = [
    { label: "Clinic" },
    { label: "City, State" },
    { label: "Serial" },
    { label: "Balance due", num: true },
    { label: "Balance status" },
    { label: "Invoice" },
    { label: "Ship by" },
    { label: "Action" },
  ];

  const tableRows = rows.map((row) => {
    const action = actionFor(row);
    return {
      id: row.orderId,
      cells: [
        <>
          {row.clinicName || "—"}
          <Sub2>{row.doctorName}</Sub2>
        </>,
        [row.city, row.state].filter(Boolean).join(", ") || "—",
        row.serial ? <Oid>{row.serial}</Oid> : <Tag tone="i">Not allocated</Tag>,
        inr(row.balanceDue),
        row.balanceCleared ? (
          <Tag tone="g">Cleared {shortDate(row.balanceClearedAt)}</Tag>
        ) : row.stage === "Dispatched" ? (
          <Tag tone="b">Shipped, unpaid</Tag>
        ) : row.held ? (
          <Tag tone="b">Overdue, held</Tag>
        ) : row.balanceRequested ? (
          <Tag tone="s">Requested</Tag>
        ) : (
          <Tag tone="i">Not requested</Tag>
        ),
        row.invoiceRaised ? (
          <Tag tone="g">Raised {shortDate(row.invoiceRaisedAt)}</Tag>
        ) : row.readyToShip ? (
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActive({ ...toItem(row, "GENERATE_INVOICE"), screenKey: "generate-invoice" });
            }}
            sx={btnSx("ghost")}
          >
            Generate invoice
          </Box>
        ) : (
          <Tag tone="i">Not ready yet</Tag>
        ),
        row.stage === "Dispatched" ? (
          `Shipped ${shortDate(row.dispatchDate)}`
        ) : row.held ? (
          <Box component="span" sx={{ color: UI.red, fontWeight: 500 }}>
            Overdue
          </Box>
        ) : (
          shortDate(row.shippingDate)
        ),
        action ? (
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (action.direct) {
                handleResend(row);
              } else {
                setActive({ ...toItem(row, action.id), screenKey: action.screen });
              }
            }}
            disabled={action.direct && resendingId === row.orderId}
            sx={btnSx(action.variant)}
          >
            {action.direct && resendingId === row.orderId ? "Resending" : action.label}
          </Box>
        ) : (
          "—"
        ),
      ],
    };
  });

  const kpis = summary
    ? [
        { label: "In build", value: summary.inBuild },
        { label: "Held on payment", value: summary.held, alarm: true },
        { label: "Awaiting dates", value: summary.awaitingDates },
        { label: "Clear to dispatch", value: summary.clearToDispatch },
        { label: "Shipped, unpaid", value: summary.shippedUnpaid, alarm: summary.shippedUnpaid > 0 },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Ready to ship"
        lede="Units built or building. Dispatch is gated on the balance clearing."
        kpis={kpis}
      />

      {summary?.held > 0 && (
        <Notice>
          <b>Open decision.</b> Hold rule and release authority are undefined.
          Who can dispatch against an unpaid balance, and up to what value?
        </Notice>
      )}

      <DashTable
        columns={columns}
        rows={tableRows}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="Nothing in build yet."
        errorText={loadFailed ? "Could not load the build list." : null}
        onRetry={() => fetchRows(page)}
        foot={
          summary
            ? `${summary.inBuild ?? rows.length} in build · ${
                summary.held ?? 0
              } held on payment`
            : null
        }
      />

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <ActionDrawer
        open={Boolean(active)}
        screenKey={active?.screenKey}
        item={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchRows(page);
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default ReadyToShip;
